import moment from 'moment';
import React, {
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useState,
} from 'react';
import type { ColDef, GridOptions, GridReadyEvent } from 'ag-grid-community';
import { useParams } from 'react-router-dom';

import MetricOptionsContext from '../../../contexts/MetricOptionsContext';
import isDefined from '../../../isDefined';
import DataTable from '../../../components/DataTable';
import useIsDataTable from '../../../components/DataTable/useIsDataTable';
import Grid from '../../../components/Grid/Grid';
import buildGridOptions from '../../../components/Grid/buildGridOptions';
import Loading from '../../../components/Loading/Loading';
import AccountContext from '../../../contexts/AccountContext';
import FlexCentered from '../../../components/Common/FlexCentered';
import formatDateLabel from '../../../components/V5Gadget/formatDateLabel';
import GoalContext from '../../../contexts/GoalContext';
import visTypeCheckers from '../../../types/visTypeCheckers';
import WallboardContext from 'contexts/WallboardContext';
import { RowHeightContext } from '../../../contextProviders/SplashScreenProviders/MiscProviders/RowHeightProvider';
import { AccountTodayOverrideContext } from '../../../contextProviders/SplashScreenProviders/UserAndAccountProviders/AccountTodayOverrideProvider';

import { KpiNameRenderer, TrendCellRenderer } from './cellRenderers';
import { KPI_NAME_RENDERER, TREND_CELL_RENDERER } from './constants';

const handleSuppressKeyboardEvent = () => true;

const useColDefs = ({
  isCard,
  primaryTrend,
}: {
  isCard?: boolean;
  primaryTrend?: Goals.MetricResult;
}) => {
  const { dateBuckets, goal } = useContext(GoalContext);
  const { isDemoAccount, weekStartsOn } = useContext(AccountContext);
  const { todayOverride } = useContext(AccountTodayOverrideContext);
  const { goalId } = useParams<{ goalId?: string }>();
  const isList = goalId === undefined;
  const [colDefs, setColDefs] = useState<ColDef[] | undefined>();

  useEffect(() => {
    if (!primaryTrend || !primaryTrend.trend) {
      return;
    }
    const metricColDef = {
      headerName: '',
      field: 'metricName',
      pinned: 'left' as 'left',
      minWidth: 300,
      flex: 1,
      suppressHeaderMenuButton: true,
      cellRenderer: KPI_NAME_RENDERER,
      cellRendererParams: {
        isCard,
      },
    };

    const dateColDefs = (() => {
      if (isList) {
        const now = moment
          .utc(todayOverride)
          .startOf('day')
          .format('YYYY-MM-DD');
        const goalDataInPast = primaryTrend.trend.filter((t) => t.date < now);
        const offset = visTypeCheckers.isGeneralGoal(goal) ? 2 : 1;
        if (primaryTrend.trend && goalDataInPast.length >= offset) {
          const trendItemToUse = goalDataInPast[goalDataInPast.length - offset];
          return [
            {
              key: moment(trendItemToUse.date).format('YYYY-MM-DD'),
              field: trendItemToUse.date,
              headerName:
                goal.cadence === 'month'
                  ? moment(trendItemToUse.date).format("MMM 'YY")
                  : formatDateLabel(trendItemToUse.date, goal.cadence, true),
              minWidth: 120,
              wrapHeaderText: true,
              autoHeaderHeight: true,
              headerClass: 'rightAlignedHeader',
              suppressHeaderMenuButton: true,
              flex: 1,
              cellRenderer: TREND_CELL_RENDERER,
              cellRendererParams: {
                date: trendItemToUse.date,
                isCard,
              },
              cellStyle: { justifyContent: 'flex-end' },
            },
          ];
        } else {
          return [];
        }
      } else {
        return dateBuckets.map((date) => {
          return {
            key: moment(date).format('YYYY-MM-DD'),
            field: date,
            headerName:
              goal.cadence === 'month'
                ? moment(date).format("MMM 'YY")
                : formatDateLabel(date, goal.cadence, true),
            minWidth: 120,
            wrapHeaderText: true,
            autoHeaderHeight: true,
            headerClass: 'rightAlignedHeader',
            suppressHeaderMenuButton: true,
            flex: 1,
            cellRenderer: TREND_CELL_RENDERER,
            cellRendererParams: {
              date,
              isCard,
            },
            cellStyle: { justifyContent: 'flex-end' },
          };
        });
      }
    })();

    setColDefs([metricColDef, ...dateColDefs]);
  }, [
    isCard,
    isDemoAccount,
    isList,
    dateBuckets,
    weekStartsOn,
    primaryTrend,
    goal.cadence,
    goal,
    todayOverride,
  ]);

  return colDefs;
};

const useRows = ({
  kpis,
  kpiResults,
  topGoalRow,
}: {
  kpis: GoalKPI[];
  kpiResults?: Goals.MetricResult[];
  topGoalRow?: {
    result: Goals.GoalMetricResult;
    metric: Metrics.Metric;
  };
}) => {
  const { dateBuckets } = useContext(GoalContext);
  const { unitsLocale, weekStartsOn } = useContext(AccountContext);
  const { metricOptions } = useContext(MetricOptionsContext);
  const [rowData, setRowData] = useState<any[]>([]);

  useEffect(() => {
    if (!kpiResults) {
      return;
    }

    const newRowData = [] as any[];

    if (topGoalRow) {
      // Goal row
      const goalRow = {
        unitsLocale,
        metricName: topGoalRow.metric.name,
        rowType: 'goal',
      } as {
        [key: string]:
          | null
          | undefined
          | number
          | string
          | Goals.MetricResult
          | GoalKPI
          | boolean
          | 'goal'
          | 'kpi heading'
          | 'kpi';
      };
      dateBuckets.forEach((date) => {
        if (topGoalRow.result.trend) {
          const trendItem = topGoalRow.result.trend.find(
            (t) => t.date === date,
          );
          if (trendItem) {
            goalRow[date] = trendItem.value;
          } else {
            goalRow[date] = null;
          }
        } else {
          goalRow[date] = null;
        }
      });
      newRowData.push(goalRow);
    }

    const kpiRows = kpiResults
      .map((kpi) => {
        const kpiSetting = kpis.find((setting) => setting.id === kpi.id);
        if (!kpiSetting) {
          return undefined;
        }
        const metric = metricOptions.find(
          (mo) => mo.id === kpiSetting.metricId,
        );
        if (!metric || !kpi.trend || !kpiSetting) {
          return undefined;
        }
        const row = {
          kpi,
          kpiSetting,
          metric,
          unitsLocale,
          metricName: kpiSetting.name ? kpiSetting.name : metric.name,
          rowType: 'kpi',
        } as {
          [key: string]:
            | null
            | undefined
            | number
            | string
            | Goals.MetricResult
            | GoalKPI
            | Metrics.Metric;
        };
        dateBuckets.forEach((date) => {
          if (kpi.trend) {
            const trendItem = kpi.trend.find((t) => t.date === date);
            if (trendItem) {
              row[date] = trendItem.value;
            } else {
              row[date] = null;
            }
          } else {
            row[date] = null;
          }
        });

        return row;
      })
      .filter(isDefined);

    newRowData.push(...kpiRows);
    setRowData(newRowData);
  }, [
    weekStartsOn,
    metricOptions,
    unitsLocale,
    dateBuckets,
    kpiResults,
    topGoalRow,
    kpis,
  ]);

  return rowData;
};

// This table's own renderers, by the names its colDefs use. Not the shared
// registry (DataTable.renderers): that registers these, so importing it here
// would make an import cycle.
const GOAL_KPI_RENDERERS = {
  [KPI_NAME_RENDERER]: KpiNameRenderer,
  [TREND_CELL_RENDERER]: TrendCellRenderer,
};

const GoalKpiVis = ({
  isCard,
  primaryTrend,
  kpis,
  kpiResults,
  topGoalRow,
}: {
  isCard?: boolean;
  primaryTrend?: Goals.MetricResult;
  kpis: GoalKPI[];
  kpiResults?: Goals.MetricResult[];
  topGoalRow?: {
    result: Goals.GoalMetricResult;
    metric: Metrics.Metric;
  };
}) => {
  const { rowHeight } = useContext(RowHeightContext);
  const { goal, dateBuckets } = useContext(GoalContext);
  const { todayOverride } = useContext(AccountTodayOverrideContext);
  const { isWallboard } = useContext(WallboardContext);
  const colDefs = useColDefs({
    isCard,
    primaryTrend,
  });
  const rowData = useRows({ kpis, kpiResults, topGoalRow });

  const createGridOptions = useCallback(() => {
    return {
      ...buildGridOptions({
        rowModelType: 'clientSide',
        rowHeight,
      }),
      groupDisplayType: 'custom' as 'custom',
      suppressAggFuncInHeader: true,
      columnDefs: colDefs,
      suppressCellFocus: true,
      domLayout: 'autoHeight' as 'autoHeight',
      defaultColDef: {
        suppressKeyboardEvent: handleSuppressKeyboardEvent,
      },
    };
  }, [colDefs, rowHeight]);
  const [gridOptions, setGridOptions] = useState<GridOptions>(() =>
    createGridOptions(),
  );

  useEffect(() => {
    setGridOptions(createGridOptions());
  }, [createGridOptions]);

  const isDataTable = useIsDataTable('goalKpis');
  // Unique per card: a page can show several goals' KPI tables
  const tableId = useId();
  // The current period's column (its field is the date), scrolled into view
  const dateToScrollTo = useMemo(() => {
    const today = moment(todayOverride).format('YYYY-MM-DD');
    let dateToUse = dateBuckets[0];
    dateBuckets.forEach((date) => {
      if (today >= date) {
        dateToUse = date;
      }
    });

    return dateToUse;
  }, [dateBuckets, todayOverride]);

  const onGridReady = useCallback(
    (event: GridReadyEvent) => {
      const col = event.api.getColumn(dateToScrollTo);

      if (col) {
        setTimeout(() => {
          event.api.ensureColumnVisible(col);
        }, 1000);
        setTimeout(() => {
          event.api.ensureColumnVisible(col);
        }, 2000);
      }
    },
    [dateToScrollTo],
  );

  if (!colDefs) {
    return (
      <FlexCentered style={{ height: 50 + goal.kpis.length * 32 }}>
        <Loading />
      </FlexCentered>
    );
  }

  if (isDataTable) {
    return (
      <div style={{ fontSize: isWallboard ? '1.2em' : '12px' }}>
        <DataTable
          tableId={tableId}
          testId="grid"
          columnDefs={colDefs}
          components={GOAL_KPI_RENDERERS}
          rows={rowData}
          // Flex columns over their minimum widths, so a long goal scrolls
          // sideways to the current period
          sizing="fitWidth"
          // As tall as its KPIs, as the AG view's domLayout autoHeight
          layout="natural"
          scrollToColumnId={dateToScrollTo}
          hasDarkHeader
        />
      </div>
    );
  }

  return (
    <div style={{ fontSize: isWallboard ? '1.2em' : '12px' }}>
      <Grid
        onGridReady={onGridReady}
        columnDefs={colDefs}
        gridOptions={gridOptions}
        gridId="performance-fleet"
        rowData={rowData}
        totalsRow={undefined}
        isBoard
        disableAutoLayout
        isPerformanceSettings
        layoutOnModelUpdated={true}
        layoutOnColumnChange={true}
        layoutOnFirstRender={true}
        hasBorders={false}
        hasDarkHeader
      />
    </div>
  );
};

export default GoalKpiVis;
